'use client'

import React from 'react'

type Props = {
  items?: Array<{ text: string; id?: string | null }>
  speed?: 'slow' | 'normal' | 'fast' | null
}

const speedMap = {
  slow: '80s',
  normal: '45s',
  fast: '25s',
}

// Figma Frame 714 (4022:82020 desktop · 4035:75700 tablet · 4035:75711 mobile) — the
// awards bar that sits inside the Home hero frame, flush against its bottom edge.
//   width    the content column (max-w 1600 with --spacing-container padding), NOT full bleed
//   border   border-t 1px theme/border (#e0dfe0)
//   fill     theme/background #ffffff  (it was rendering as a full-bleed BLACK strip)
//   padding  py section/2xs (16)
//   type     Body/bodyXsAlt — Lato 400, 12/14, UPPERCASE, theme/foreground #000
//   gap      gap/gap-8 (32) between items
//   feather  48px gradients from theme/background → transparent at each end (4035:75699)
// Figma's text nodes are trimmed to the cap box (text-box-trim), which is what makes the
// bar exactly 41px tall: 16 + 9 (cap height of 12px Lato) + 16.
export function AwardsTickerBlock({ items, speed }: Props) {
  if (!items?.length) return null

  const duration = speedMap[speed ?? 'normal']

  // Triple items for seamless infinite loop. The 32px gap is applied as trailing
  // padding rather than `gap` so each of the three sets is exactly one third of the
  // track width — the -33.333% keyframe then loops without drift.
  const tripled = [...items, ...items, ...items]

  return (
    <div className="mx-auto max-w-[var(--container-content)] px-[var(--spacing-container)]">
      <div
        className="relative overflow-hidden select-none border-t border-border bg-background ticker-anim-parent"
        aria-hidden="true"
        data-visual="awards-ticker"
      >
        <div
          className="flex w-max whitespace-nowrap ticker-anim"
          style={{ ['--ticker-duration' as any]: duration }}
        >
          {tripled.map((item, i) => (
            <span
              key={i}
              className="shrink-0 pr-8 py-[var(--spacing-section-2xs)] text-body-xs font-normal uppercase text-foreground [text-box-edge:cap_alphabetic] [text-box-trim:trim-both]"
            >
              {item.text}
            </span>
          ))}
        </div>

        {/* Feathered ends — 48px, theme/background → transparent */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-background to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-background to-transparent" />
      </div>
    </div>
  )
}
